import { PermissionFe, PermissionMap } from '@/services/service-permission.service'

/** 毒霸 AI 助手订阅市场详情页路由 */
export const AI_ASSISTANT_DETAIL_PATH = '/dashboard/ai-assistant-detail'

/**
 * AI 助手权益：price_id = permission_group_id，permission_name 与后端权益 key 一致。
 * 团队版 AI 相关 price_id / permission_name 唯一出口。
 * 91025 / 91026 / 91027 依次为洞察版 / 研判版 / 取证版（见 PermissionFe 注释、subscribe-price-title.messages.ts）。
 */
export interface AiAssistantServiceDef {
  priceId: number
  permissionName: string
}

export const AI_ASSISTANT_SERVICES: readonly AiAssistantServiceDef[] = [
  { priceId: 91025, permissionName: PermissionFe.ai_assistant_insight },
  { priceId: 91026, permissionName: PermissionFe.ai_assistant_research },
  { priceId: 91027, permissionName: PermissionFe.ai_assistant_forensic },
]

export const AI_ASSISTANT_PRICE_IDS: ReadonlySet<number> = new Set(
  AI_ASSISTANT_SERVICES.map(v => v.priceId),
)

export const AI_ASSISTANT_PERMISSION_NAMES: ReadonlySet<string> = new Set(
  AI_ASSISTANT_SERVICES.map(v => v.permissionName),
)

/** 订阅市场卡片统一使用的 AI 助手服务 price_id */
export const AI_ASSISTANT_MARKET_PRICE_ID = 90015

/** 订阅市场卡片「查看详情」：后端返回的 price_id → 详情页 */
export const AI_ASSISTANT_DETAIL_LINK_BY_PRICE_ID: Readonly<Record<number, string>> = {
  [AI_ASSISTANT_MARKET_PRICE_ID]: AI_ASSISTANT_DETAIL_PATH,
}

export function resolveSubscribePriceId (item: {
  price_id?: number
  package_price_id?: number
}): number | undefined {
  return item.price_id ?? item.package_price_id
}

export function isAiAssistantPriceId (priceId: number): boolean {
  return AI_ASSISTANT_PRICE_IDS.has(priceId)
}

export type AiAssistantAccessState = 'not_purchased' | 'active' | 'expired'

/** 根据团队权益 map 判断 AI 助手：未购买 / 生效中 / 已过期（曾购买） */
export function resolveAiAssistantAccessState (
  permissionMap: PermissionMap,
  nowSec = Math.floor(Date.now() / 1000),
): AiAssistantAccessState {
  const details = AI_ASSISTANT_SERVICES
    .map(s => permissionMap[s.permissionName])
    .filter(d => d?.permission_name)

  if (!details.length) {
    return 'not_purchased'
  }
  const hasActive = details.some(d => (d.expire_time ?? 0) > nowSec)
  return hasActive ? 'active' : 'expired'
}

/** 已购 AI 助手档位中最大的订阅到期时间（Unix 秒），供宿主 bridge 同步给龙虾展示 */
export function resolveAiAssistantSubscriptionExpireAt (permissionMap: PermissionMap): number | null {
  let maxExpire = 0
  let found = false
  for (const s of AI_ASSISTANT_SERVICES) {
    const detail = permissionMap[s.permissionName]
    if (!detail?.permission_name) continue
    found = true
    const exp = detail.expire_time ?? 0
    if (exp > maxExpire) maxExpire = exp
  }
  if (!found || maxExpire <= 0) return null
  return maxExpire
}

export function isAiAssistantSubscribeItem (item: {
  price_id?: number
  package_price_id?: number
  permission_name?: string
}): boolean {
  if (item.permission_name && AI_ASSISTANT_PERMISSION_NAMES.has(item.permission_name)) {
    return true
  }
  const id = resolveSubscribePriceId(item)
  return id != null && AI_ASSISTANT_PRICE_IDS.has(id)
}
